import React from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * MathText — renders a string that may contain inline LaTeX math.
 *
 * Wrap math expressions in dollar signs: $\frac{308}{317}$
 * Everything outside dollar signs is rendered as plain text.
 *
 * To include a literal dollar sign (e.g. currency) inside text that also
 * contains math, escape it with a backslash in the data: \$11 per day.
 * It will display as "$" without triggering KaTeX parsing.
 */
function MathText({ text, style, className }) {
  if (!text) return null;

  // Replace escaped dollar signs (\$) with a private-use placeholder so they
  // are never treated as KaTeX delimiters.
  const PLACEHOLDER = '\uE001';
  const processed = text.replace(/\\\$/g, PLACEHOLDER);

  const restore = (str) => str.replace(/\uE001/g, '$');

  const parts = processed.split(/(\$[^$]+\$)/g);

  if (parts.length === 1) {
    // No math delimiters — fast path, no KaTeX cost.
    return <span style={style} className={className}>{restore(processed)}</span>;
  }

  return (
    <span style={style} className={className}>
      {parts.map((part, i) => {
        if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
          const latex = part.slice(1, -1);
          try {
            const html = katex.renderToString(latex, {
              throwOnError: false,
              displayMode: false,
              output: 'html',
            });
            return (
              <span
                key={i}
                dangerouslySetInnerHTML={{ __html: html }}
                style={{ verticalAlign: 'middle' }}
              />
            );
          } catch (e) {
            // Fallback to raw text if KaTeX can't parse it.
            return <span key={i}>{restore(part)}</span>;
          }
        }
        return part ? <span key={i}>{restore(part)}</span> : null;
      })}
    </span>
  );
}

export default MathText;
